import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { formatDayLabel } from '@/lib/dates'
import { HUB_STATUS_ORDER, OPEN_HUB_STATUSES, hubStatusLabel, hubStatusVariant } from '@/lib/linearStatus'
import { useClientDashboard, useClientLinearIssues } from '@/queries/clientDashboards'
import type { ClientLinearIssue, HubStatus } from '@/types/linearClient'
import { CountChip, DashCard, pillClass, tdClass, thClass } from './dashboardParts'
import { LoadError } from './LoadError'
import { clientTabPath } from './tabs'

export type LinearFilter = 'open' | HubStatus

export const isLinearFilter = (value: unknown): value is LinearFilter => value === 'open' || HUB_STATUS_ORDER.includes(value as HubStatus)

function matches(issue: ClientLinearIssue, filter: LinearFilter, allReleased: boolean) {
  if (filter === 'open') return OPEN_HUB_STATUSES.includes(issue.status)
  if (filter === 'released') return issue.status === 'released' && (allReleased || issue.recentRelease)
  return issue.status === filter
}

function filterLabel(filter: LinearFilter, clientName: string) {
  if (filter === 'open') return 'Open'
  if (filter === 'released') return 'Released, last 90 days'
  return hubStatusLabel(filter, clientName)
}

function sourcesTitle(sources: ClientLinearIssue['calls']): string {
  if (sources.every(source => source.kind === 'call')) return `Raised on calls on ${sources.map(source => formatDayLabel(source.date)).join(', ')}`
  return `Raised on ${sources.map(source => `${source.kind === 'email' ? 'an email' : 'a call'} on ${formatDayLabel(source.date)}`).join(', ')}`
}

function CallSource({ issue, companyId }: { issue: ClientLinearIssue; companyId: string }) {
  const [first, ...more] = issue.calls
  if (!first) return null
  const isEmail = first.kind === 'email'
  const label = isEmail ? `Email, ${formatDayLabel(first.date)}, ${first.subject ?? 'No subject'}` : `Call, ${formatDayLabel(first.date)}`
  return (
    <Link
      to={clientTabPath(companyId, 'meetings') as never}
      search={(isEmail ? { email: first.emailId, request: first.requestId } : { meeting: first.meetingId, request: first.requestId }) as never}
      title={sourcesTitle(issue.calls)}
      className="block font-medium text-etn-accent-text-active hover:underline"
    >
      {`${label}${more.length ? ` (+${more.length})` : ''}`}
    </Link>
  )
}

// Where an issue came from: its help desk ticket first, then any calls or emails that raised it.
function Source({ issue, companyId }: { issue: ClientLinearIssue; companyId: string }) {
  if (issue.source.kind === 'linear' && !issue.calls.length) return <span className="text-etn-text-soft">Raised in Linear</span>
  return (
    <>
      {issue.source.kind === 'ticket' && (
        <Link to={`/admin/tickets/${issue.source.ticketId}` as never} className="block font-medium text-etn-accent-text-active hover:underline">
          {issue.source.ref}
        </Link>
      )}
      <CallSource issue={issue} companyId={companyId} />
    </>
  )
}

export function LinearTicketsView({ companyId, initialStatus = 'open' }: { companyId: string; initialStatus?: LinearFilter }) {
  const issues = useClientLinearIssues(companyId)
  const clientName = useClientDashboard(companyId).data?.data.companyName ?? 'the client'
  const [filter, setFilter] = useState<LinearFilter>(initialStatus)
  const [allReleased, setAllReleased] = useState(false)

  if (issues.isError) return <LoadError title="Linear issues didn't load" onRetry={() => issues.refetch()} />
  const data = issues.data?.data
  if (!data) return null

  if (!data.linked) {
    return (
      <DashCard title="Linear Tickets">
        <div className="px-[18px] py-8 text-center">
          <p className="font-medium text-etn-text">{clientName} isn&apos;t linked to a Linear customer yet</p>
          <p className="mt-1 text-[13px] text-etn-text-soft">
            Link it under Linear customers on the{' '}
            <Link to={'/admin/settings' as never} className="font-medium text-etn-accent-text-active hover:underline">
              Settings page
            </Link>{' '}
            to see its issues here.
          </p>
        </div>
      </DashCard>
    )
  }

  const count = (key: LinearFilter) => data.issues.filter(issue => matches(issue, key, false)).length
  const anyIssue = (key: LinearFilter) => data.issues.some(issue => issue.status === key)
  const pills = (['open', ...HUB_STATUS_ORDER] as LinearFilter[]).filter(key => key === 'open' || key === filter || count(key) > 0 || (key === 'released' && anyIssue(key)))
  const visible = data.issues.filter(issue => matches(issue, filter, allReleased))
  const allReleasedCount = data.issues.filter(issue => issue.status === 'released').length

  return (
    <DashCard
      title="Linear Tickets"
      subtitle={`Issues carrying ${data.customerName ?? clientName} in Linear · newest first`}
      right={
        <div className="flex flex-wrap gap-1.5">
          {pills.map(key => (
            <button key={key} type="button" className={pillClass(filter === key)} onClick={() => setFilter(key)}>
              <span>{filterLabel(key, clientName)}</span> <CountChip>{count(key)}</CountChip>
            </button>
          ))}
        </div>
      }
    >
      {filter === 'released' && !allReleased && allReleasedCount > count('released') && (
        <div className="border-b border-[var(--etn-gray-100)] px-[18px] py-2.5">
          <button type="button" className="text-[12.5px] font-medium text-etn-accent-text-active hover:underline" onClick={() => setAllReleased(true)}>
            Show all released ({allReleasedCount})
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['Issue', 'Status', 'Raised', 'Came from'].map(heading => (
                <th key={heading} scope="col" className={thClass}>
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-etn-border-row">
            {visible.map(issue => (
              <tr key={issue.identifier} data-testid="linear-issue-row" className="hover:bg-etn-row-hover">
                <td className={`${tdClass} min-w-[300px]`}>
                  <span className="mr-2 whitespace-nowrap text-[12px] font-semibold text-etn-text-label">{issue.identifier}</span>
                  <a href={issue.url} target="_blank" rel="noreferrer" className="font-medium text-etn-text hover:text-etn-accent">
                    {issue.title}
                  </a>
                </td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  <Badge variant={hubStatusVariant[issue.status]}>{hubStatusLabel(issue.status, clientName)}</Badge>
                </td>
                <td className={`${tdClass} whitespace-nowrap text-etn-text-soft`}>{formatDayLabel(issue.raisedAt)}</td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  <Source issue={issue} companyId={companyId} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && <p className="px-[18px] py-6 text-center text-[13px] text-etn-text-soft">No issues with this status</p>}
      </div>
    </DashCard>
  )
}
